import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Edit2, Trash2, AlertTriangle, Package, RefreshCw, Upload, Eye, CheckSquare, Search } from "lucide-react";
import { useSortableTable } from "@/hooks/useSortableTable";
import { EditableCell } from "@/components/EditableCell";
import { Checkbox } from "@/components/ui/checkbox";
import InvoiceUpload from "@/components/InvoiceUpload";
import ProductDetail from "@/components/ProductDetail";
import { PageControls } from "@/components/PageControls";

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [dialog, setDialog] = useState(null);
  const [restockDialog, setRestockDialog] = useState(null);
  const [restockBottles, setRestockBottles] = useState(0);
  const [restockUses, setRestockUses] = useState(0);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({ name: '', sku: '', brand: '', category: 'General', unit_cost: 0, reorder_level: 5, stock_on_hand: 0, max_uses_per_bottle: 70, uses_on_hand: 0, reorder_level_uses: 10, active: true });

  const load = async () => {
    try {
      const params = new URLSearchParams({ limit: String(pageSize), skip: String((page - 1) * pageSize) });
      if (search.trim()) params.append('search', search.trim());
      const res = await axios.get(`${API}/inventory/products?${params}`);
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
    } catch (e) {  }
  };

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { load(); }, [search, page, pageSize]);

  const { sortedData, SortHeader } = useSortableTable(products, 'name');

  const saveProduct = async () => {
    try {
      if (dialog === 'new') {
        await axios.post(`${API}/inventory/products`, form);
        toast.success('Product added');
      } else {
        await axios.put(`${API}/inventory/products/${dialog.id}`, form);
        toast.success('Product updated');
      }
      setDialog(null);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const saveInline = async (productId, field, value) => {
    try {
      const numVal = ['uses_on_hand', 'reorder_level_uses', 'max_uses_per_bottle', 'unit_cost', 'stock_on_hand'].includes(field) 
        ? parseFloat(value) || 0 : value;
      await axios.put(`${API}/inventory/products/${productId}`, { [field]: numVal });
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, [field]: numVal } : p));
      toast.success('Updated');
    } catch (e) { toast.error('Failed'); }
  };

  const doRestock = async () => {
    if (!restockDialog) return;
    try {
      await axios.post(`${API}/inventory/products/${restockDialog.id}/restock`, {
        bottles: restockBottles, uses: restockUses
      });
      toast.success('Restocked!');
      setRestockDialog(null);
      setRestockBottles(0);
      setRestockUses(0);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete(`${API}/inventory/products/${id}`); toast.success('Deleted'); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); load(); } catch (e) { toast.error('Failed'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === sortedData.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedData.map(p => p.id)));
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected products?`)) return;
    try {
      await axios.post(`${API}/inventory/products/bulk-delete`, { ids: [...selected] });
      toast.success(`${selected.size} products deleted`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const lowUsesCount = products.filter(p => (p.uses_on_hand || 0) <= (p.reorder_level_uses || 10) && p.active).length;

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Inventory</h1>
          <p className="text-sm text-muted-foreground">{total} products · {lowUsesCount > 0 ? <span className="text-red-500 font-medium">{lowUsesCount} low stock</span> : 'All stocked'} · Click cells to edit</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="destructive" onClick={bulkDelete} className="rounded-xl">
              <Trash2 size={14} className="mr-1" /> Delete {selected.size} selected
            </Button>
          )}
          <Button variant="outline" onClick={() => setInvoiceOpen(true)} className="rounded-xl">
            <Upload size={16} className="mr-1" /> Upload Invoice
          </Button>
          <Button onClick={() => { setForm({ name: '', sku: '', brand: '', category: 'General', unit_cost: 0, reorder_level: 5, stock_on_hand: 0, max_uses_per_bottle: 70, uses_on_hand: 0, reorder_level_uses: 10, active: true }); setDialog('new'); }} className="rounded-xl">
            <Plus size={16} className="mr-1" /> Add Product
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…" className="pl-9 rounded-xl h-9 text-sm" />
      </div>

      <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="p-3 w-8">
                  <Checkbox checked={selected.size === sortedData.length && sortedData.length > 0} onCheckedChange={toggleSelectAll} />
                </th>
                <SortHeader field="name">Product</SortHeader>
                <SortHeader field="unit_cost">Unit Cost</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground text-xs">Max Uses</th>
                <th className="text-left p-3 font-medium text-muted-foreground text-xs">Cost/Use</th>
                <SortHeader field="uses_on_hand">Uses Left</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground text-xs">Reorder At</th>
                <SortHeader field="stock_on_hand">Bottles</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(p => {
                const isLow = (p.uses_on_hand || 0) <= (p.reorder_level_uses || 10);
                const costPerUse = p.cost_per_use || (p.max_uses_per_bottle > 0 ? (p.unit_cost || 0) / p.max_uses_per_bottle : 0);
                return (
                  <tr key={p.id} className={`border-b border-border/50 hover:bg-muted/10 transition-colors ${!p.active ? 'opacity-40' : ''} ${selected.has(p.id) ? 'bg-primary/5' : ''}`}>
                    <td className="p-1.5 pl-3">
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} />
                    </td>
                    <td className="p-1.5">
                      <div className="flex items-center gap-1.5">
                        {isLow && p.active && <AlertTriangle size={13} className="text-red-500 shrink-0" />}
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          {p.brand && <p className="text-[10px] text-muted-foreground">{p.brand}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-1.5">
                      <EditableCell value={p.unit_cost} field="unit_cost" type="number"
                        onSave={(f, v) => saveInline(p.id, f, v)}
                        displayRender={v => <span className="tabular-nums text-xs">£{(parseFloat(v) || 0).toFixed(2)}</span>} />
                    </td>
                    <td className="p-1.5">
                      <EditableCell value={p.max_uses_per_bottle || 70} field="max_uses_per_bottle" type="number"
                        onSave={(f, v) => saveInline(p.id, f, v)}
                        displayRender={v => <span className="tabular-nums text-xs">{v}</span>} />
                    </td>
                    <td className="p-1.5 text-xs tabular-nums text-muted-foreground">£{costPerUse.toFixed(3)}</td>
                    <td className="p-1.5">
                      <EditableCell value={p.uses_on_hand || 0} field="uses_on_hand" type="number"
                        onSave={(f, v) => saveInline(p.id, f, v)}
                        displayRender={v => (
                          <Badge className={`text-xs rounded-full tabular-nums ${isLow && p.active ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {v} uses {isLow && p.active ? '(low)' : ''}
                          </Badge>
                        )} />
                    </td>
                    <td className="p-1.5">
                      <EditableCell value={p.reorder_level_uses || 10} field="reorder_level_uses" type="number"
                        onSave={(f, v) => saveInline(p.id, f, v)}
                        displayRender={v => <span className="text-xs tabular-nums">{v}</span>} />
                    </td>
                    <td className="p-1.5 text-xs tabular-nums text-center">{p.stock_on_hand || 0}</td>
                    <td className="p-1.5">
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" onClick={() => setDetailProduct(p)} className="h-7 w-7 rounded-lg" title="View details"><Eye size={11} /></Button>
                        <Button variant="secondary" size="sm" onClick={() => { setRestockDialog(p); setRestockBottles(0); setRestockUses(0); }} className="h-7 rounded-lg text-[10px] px-2">
                          <RefreshCw size={11} className="mr-1" /> Restock
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setForm({...p}); setDialog(p); }} className="h-7 w-7 rounded-lg"><Edit2 size={11} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="h-7 w-7 rounded-lg text-destructive"><Trash2 size={11} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sortedData.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-muted-foreground">No products yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <PageControls page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />

      {/* Add/Edit Product Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}>{dialog === 'new' ? 'Add Product' : 'Edit Product'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">SKU</Label><Input value={form.sku} onChange={e => setForm(p => ({...p, sku: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Brand</Label><Input value={form.brand} onChange={e => setForm(p => ({...p, brand: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Unit Cost (£ per bottle)</Label><Input type="number" step="0.01" value={form.unit_cost} onChange={e => setForm(p => ({...p, unit_cost: parseFloat(e.target.value) || 0}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Max Uses per Bottle</Label><Input type="number" value={form.max_uses_per_bottle || 70} onChange={e => setForm(p => ({...p, max_uses_per_bottle: parseInt(e.target.value) || 70}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Uses On Hand</Label><Input type="number" value={form.uses_on_hand || 0} onChange={e => setForm(p => ({...p, uses_on_hand: parseInt(e.target.value) || 0}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Reorder Level (Uses)</Label><Input type="number" value={form.reorder_level_uses || 10} onChange={e => setForm(p => ({...p, reorder_level_uses: parseInt(e.target.value) || 10}))} className="mt-1 rounded-xl" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} /><Label className="text-xs">Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={saveProduct} className="rounded-xl">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={!!restockDialog} onOpenChange={() => setRestockDialog(null)}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}>Restock: {restockDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-xl text-xs">
              <p>Current: <strong>{restockDialog?.uses_on_hand || 0} uses</strong> ({restockDialog?.stock_on_hand || 0} bottles)</p>
              <p>Max uses/bottle: <strong>{restockDialog?.max_uses_per_bottle || 70}</strong></p>
            </div>
            <div>
              <Label className="text-xs">Add Bottles</Label>
              <Input type="number" value={restockBottles} onChange={e => setRestockBottles(parseInt(e.target.value) || 0)} className="mt-1 rounded-xl" placeholder="e.g. 2" />
              {restockBottles > 0 && <p className="text-[10px] text-muted-foreground mt-1">= {restockBottles * (restockDialog?.max_uses_per_bottle || 70)} uses</p>}
            </div>
            <div>
              <Label className="text-xs">Or Add Uses Directly</Label>
              <Input type="number" value={restockUses} onChange={e => setRestockUses(parseInt(e.target.value) || 0)} className="mt-1 rounded-xl" placeholder="e.g. 35 (half bottle)" />
            </div>
            <div className="p-3 bg-green-50 rounded-xl text-xs text-green-700">
              Total to add: <strong>{(restockBottles * (restockDialog?.max_uses_per_bottle || 70)) + restockUses} uses</strong>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestockDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={doRestock} className="rounded-xl" disabled={restockBottles === 0 && restockUses === 0}>Restock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload */}
      <InvoiceUpload open={invoiceOpen} onClose={() => setInvoiceOpen(false)} onPosted={load} defaultPostTo="inventory" />
      <ProductDetail product={detailProduct} open={!!detailProduct} onClose={() => setDetailProduct(null)} onUpdated={load} />
    </div>
  );
}
